import { useTranslation } from "@coworker/locales";
import { useCallback, useMemo, useState } from "react";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import {
  Item,
  ItemNoAndType,
} from "@coworker/apprestructured/src/shared/types/item";
import { FixaToast } from "@coworker/apprestructured/src/shared/wrappers/FixaToast/FixaToast";
import { LoadingArticle } from "./LoadingArticle/LoadingArticle";
import { SelectedArticles } from "./SelectedArticles/SelectedArticles";
import { SelectedArticlesHeader } from "./SelectedArticles/SelectedArticlesHeader";
import ListItem from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ListItem";
import ImagePreviewAddOn from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ImagePreviewAddOn/ImagePreviewAddOn";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import ModalColumn from "@coworker/apprestructured/src/task-management/components/ModalColumn/ModalColumn";
import StyledModalFooter from "../StyledModalFooter/StyledModalFooter";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";
import DefaultScannerComponent from "@coworker/apprestructured/src/scan-and-search/DefaultScannerComponent";
import { useQueryClient } from "@tanstack/react-query";
import { getItemQueryKey } from "@coworker/apprestructured/src/shared/hooks/item/queryKeys";
import { QueryKeys } from "@coworker/app/src/components/RoomSettings/hooks/queryKeys";
import { fetchItemInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemInfo";
import { fetchItemsInfoFromDataService } from "@coworker/app/src/components/RoomSettings/services/localCombination.service";
import {
  useCountryId,
  useStoreId,
} from "@coworker/app/src/core/auth/useLoggedInUser";

type AttachArticlesProps = {
  items: Item[];
  setItemNosAndType: (newItemNosAndType: ItemNoAndType[]) => void;
  disabled: boolean;
  maxArticles: number;
};

export const AttachArticles = ({
  items,
  setItemNosAndType,
  disabled,
  maxArticles,
}: AttachArticlesProps) => {
  const [showModal, setShowModal] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [modalItems, setModalItems] = useState<Item[]>(items);

  const storeId = useStoreId();
  const countryId = useCountryId();

  const queryClient = useQueryClient();
  const {
    saveGuardedFunction: handleCloseClick,
    enableSaveGuard,
    disableSaveGuard,
  } = useSaveGuardedFunction(
    useCallback(() => {
      setShowModal(false);
      setShowToast(false);
      setToastMessage("");
    }, [])
  );

  const handleListViewItemClick = useCallback(() => {
    setShowModal(true);
    setModalItems(items);
  }, [items]);

  const { t } = useTranslation();

  const handleDeleteArticle = useCallback(
    (no: string) => {
      setModalItems((prev) => prev.filter((modalItem) => modalItem.no !== no));
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleScannedArticle = useCallback(
    async (itemNo?: string) => {
      setIsCameraOpen(false);
      setShowToast(false);

      if (!itemNo || isLoading) return;

      if (modalItems.length >= maxArticles) {
        setToastMessage("tooManyArticlesAddedString");
        setShowToast(true);
        return;
      }

      if (modalItems.some((modalItem) => modalItem.no === itemNo)) {
        setToastMessage("articleAlreadyExistsString");
        setShowToast(true);
        return;
      }

      setIsLoading(true);
      try {
        const itemInfo = await queryClient.ensureQueryData({
          queryKey: getItemQueryKey(itemNo),
          queryFn: () => fetchItemInfo(itemNo),
        });

        // If itemInfo is not found, it might be a combination article, so we check the local combination data
        const localCombinationItemInfo = itemInfo
          ? null
          : await queryClient.ensureQueryData({
              queryKey: [
                QueryKeys.CombinationArticle,
                countryId,
                storeId,
                itemNo,
              ],
              queryFn: () =>
                fetchItemsInfoFromDataService(storeId, countryId, itemNo),
            });

        const combinationItem = Array.isArray(localCombinationItemInfo)
          ? localCombinationItemInfo[0]
          : undefined;

        const itemData = itemInfo ?? (combinationItem as Item);

        if (itemData) {
          setModalItems((prev) => [...prev, itemData]);
          enableSaveGuard();
        } else {
          setToastMessage("couldNotAddArticleString");
          setShowToast(true);
        }
      } catch {
        setToastMessage("couldNotAddArticleString");
        setShowToast(true);
      } finally {
        setIsLoading(false);
      }
    },
    [
      isLoading,
      modalItems,
      maxArticles,
      enableSaveGuard,
      storeId,
      countryId,
      queryClient,
    ]
  );

  const imageSrcs = useMemo(() => {
    return items.map((item) => item.smallImageUrl ?? item.imageUrl ?? "");
  }, [items]);

  const content = useMemo(() => {
    if (isCameraOpen) {
      return (
        <DefaultScannerComponent
          onResult={({ articleNumber }) => handleScannedArticle(articleNumber)}
          isCameraOpen={isCameraOpen}
          onCameraOpenChange={setIsCameraOpen}
        />
      );
    }

    return (
      <FixaModalBody>
        <ModalColumn gap="medium">
          <DefaultScannerComponent
            onResult={({ articleNumber }) =>
              handleScannedArticle(articleNumber)
            }
            isCameraOpen={isCameraOpen}
            onCameraOpenChange={setIsCameraOpen}
          />

          {isLoading ? (
            <LoadingArticle />
          ) : (
            <div>
              <SelectedArticlesHeader numberOfArticles={modalItems.length} />
              <SelectedArticles
                items={modalItems}
                onDeleteArticle={handleDeleteArticle}
              />
            </div>
          )}
        </ModalColumn>
      </FixaModalBody>
    );
  }, [
    handleScannedArticle,
    isLoading,
    modalItems,
    handleDeleteArticle,
    isCameraOpen,
    setIsCameraOpen,
  ]);

  const handleConfirmClick = useCallback(() => {
    setItemNosAndType(modalItems.map(({ no, type }) => ({ no, type })));
    setShowModal(false);
    disableSaveGuard();
  }, [setItemNosAndType, modalItems, disableSaveGuard]);

  return (
    <>
      <ListItem
        title={t(`${items.length > 0 ? "articlesString" : "attachArticles"}`)}
        description={
          items.length > 0
            ? items.length === 1
              ? `1 ${t("articleString")}`
              : `${items.length} ${t("articlesString")}`
            : ""
        }
        disabled={disabled}
        onClick={handleListViewItemClick}
        addOn={
          imageSrcs.length > 0 ? <ImagePreviewAddOn srcs={imageSrcs} /> : null
        }
        emphasised={items.length > 0}
      />
      <FixaModal visible={showModal} handleCloseBtn={handleCloseClick}>
        <FixaSheets
          header={
            <FixaModalHeader
              title={t("attachArticles")}
              closeBtnClick={handleCloseClick}
            />
          }
          footer={
            isCameraOpen ? null : (
              <StyledModalFooter>
                <FixaButton
                  text={t("confirmString")}
                  type="primary"
                  onClick={handleConfirmClick}
                />
              </StyledModalFooter>
            )
          }
          fullSize
        >
          {content}
        </FixaSheets>
      </FixaModal>
      <FixaToast
        isOpen={showToast}
        text={t(toastMessage)}
        onCloseRequest={() => setShowToast(false)}
      />
    </>
  );
};
